<template>
  <div class="translations-management">
    <!-- Header with locale selector and actions -->
    <translations-header
      :selected-locale="state.selectedLocale"
      :locale-options="localeOptions"
      :loading="state.loading"
      :total-translations="totalTranslations"
      :missing-translations="missingTranslationsCount"
      @locale-changed="handleLocaleChange"
      @export-translations="exportTranslations"
      @show-import-modal="state.showImportModal = true"
      @show-create-locale-modal="state.showCreateLocaleModal = true"
      @refresh-translations="loadTranslations"
 />

    <template v-if="!state.loading">
      <!-- Statistics Cards -->

      <b-card>
        <!-- Global Search -->
        <global-search
          :search-term="state.globalSearch"
          :search-results="state.globalSearchResults"
          :translation-categories="translationCategories"
          @search="handleGlobalSearch"
          @clear-search="clearGlobalSearch"
          @navigate-to-result="navigateToResult" />

        <!-- Category Navigation -->
        <category-tabs
          :active-category="state.activeCategory"
          :translation-counts="state.translationCounts"
          :get-translations-for-category="getTranslationsForCategory"
          :get-translations-for-key="getTranslationsForKey"
          :available-languages="state.availableLanguages"
          @category-changed="handleCategoryChange" />
      </b-card>
      <!-- Translation Editor -->
      <translation-editor
        v-if="activeCategory"
        :title="activeCategory.name"
        :icon="activeCategory.icon || 'GlobeIcon'"
        :translations="getTranslationsForCategory(state.activeCategory)"
        :available-locales="state.availableLocales"
        :available-languages="state.availableLanguages"
        :get-translations-for-key="getTranslationsForKey"
        :get-language-code="getLanguageCode"
        :loading-locales="state.loadingLocales"
        :selected-translation-key="state.selectedTranslationKey"
        @update-translation="handleUpdateTranslation"
        @update-multi-locale-translation="handleUpdateMultiLocaleTranslation"
        @add-translation="handleAddTranslation"
        @translations-updated="handleTranslationsUpdated"
        @update-translations-in-state="handleUpdateTranslationsInState"
        @clear-selected-key="clearSelectedTranslationKey" />

      <!-- Empty State -->
      <div v-else class="text-center py-5">
        <feather-icon icon="FileTextIcon" size="48" class="text-muted mb-3" />
        <h5 class="text-muted">Brak tłumaczeń w tej kategorii</h5>
        <p class="text-muted">Rozpocznij dodawanie tłumaczeń dla kategorii {{ activeCategory.name }}</p>
      </div>
    </template>

    <!-- Loading State -->
    <div v-else class="d-flex justify-content-center align-items-center py-5">
      <div class="text-center">
        <b-spinner variant="primary" class="mb-3"></b-spinner>
        <h5 class="text-muted">Ładowanie tłumaczeń...</h5>
      </div>
    </div>

    <!-- Import Modal -->
    <import-modal
      v-model="state.showImportModal"
      :import-file.sync="state.importFile"
      :import-options.sync="state.importOptions"
      @import="handleImport"
      @reset="resetImportForm" />

    <!-- Create Locale Modal -->
    <create-locale-modal
      v-model="state.showCreateLocaleModal"
      :new-locale.sync="state.newLocale"
      :locale-options="localeOptions"
      @create-locale="handleCreateLocale"
      @reset="resetCreateLocaleForm" />
  </div>
</template>

<script>
import { computed, onMounted } from '@vue/composition-api';

// Composable
import { useTranslations } from '@/composables/useTranslations';

// Components
import TranslationsHeader from '@/components/translations/TranslationsHeader.vue';
import TranslationsStats from '@/components/translations/TranslationsStats.vue';
import GlobalSearch from '@/components/translations/GlobalSearch.vue';
import CategoryTabs from '@/components/translations/CategoryTabs.vue';
import TranslationEditor from '@/components/translations/TranslationEditor.vue';
import ImportModal from '@/components/translations/ImportModal.vue';
import CreateLocaleModal from '@/components/translations/CreateLocaleModal.vue';

export default {
  name: 'TranslationsIndex',
  components: {
    TranslationsHeader,
    TranslationsStats,
    GlobalSearch,
    CategoryTabs,
    TranslationEditor,
    ImportModal,
    CreateLocaleModal,
  },
  setup() {
    // Use the translations composable
    const {
      state,
      translationCategories,
      localeOptions,
      totalTranslations,
      missingTranslationsCount,
      loadLocales,
      loadTranslations,
      loadAllTranslations,
      handleLocaleChange,
      handleCategoryChange,
      handleGlobalSearch,
      clearGlobalSearch,
      navigateToResult,
      clearSelectedTranslationKey,
      handleUpdateTranslation,
      handleUpdateMultiLocaleTranslation,
      handleAddTranslation,
      exportTranslations,
      handleImport,
      resetImportForm,
      handleCreateLocale,
      resetCreateLocaleForm,
      handleTranslationsUpdated,
      getTranslationsForKey,
      getTranslationsForCategory,
      getLanguageCode,
      updateTranslationsInState,
    } = useTranslations();

    const activeCategory = computed(() => {
      return translationCategories[state.activeCategory] || {};
    });

    // Initialize data on component mount
    onMounted(async () => {
      await loadLocales();
      await loadAllTranslations();
    });

    // Wrapper method for handling efficient state updates
    const handleUpdateTranslationsInState = (updatedTranslations) => {
      updateTranslationsInState(updatedTranslations);
    };

    return {
      // State
      state,
      translationCategories,
      activeCategory,
      // Computed
      localeOptions,
      totalTranslations,
      missingTranslationsCount,
      // Methods
      loadTranslations,
      loadAllTranslations,
      handleLocaleChange,
      handleCategoryChange,
      handleGlobalSearch,
      clearGlobalSearch,
      navigateToResult,
      clearSelectedTranslationKey,
      handleUpdateTranslation,
      handleUpdateMultiLocaleTranslation,
      handleAddTranslation,
      exportTranslations,
      handleImport,
      resetImportForm,
      handleCreateLocale,
      resetCreateLocaleForm,
      handleTranslationsUpdated,
      getTranslationsForKey,
      getTranslationsForCategory,
      getLanguageCode,
      handleUpdateTranslationsInState,
    };
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
